import AXIOS, { AxiosResponse } from 'axios';
import * as gql from 'gql-query-builder';
import {
  filter,
  isArray,
  isObject,
  isString,
  keys,
  map,
  mapValues,
  transform,
} from 'lodash';

import { useEffect, useState } from 'react';
import { ValidationError } from '../models/forms/form';
import { API_URL } from './constants';
import { toData, toType, toTypeList } from './serialization';

const axios = AXIOS.create({
  baseURL: API_URL,
  withCredentials: true,
  validateStatus: (status: number) =>
    (status >= 200 && status < 300) || status == 400 || status == 403,
});

export type GraphqlOperation = 'query' | 'mutation';

export interface Response<T> {
  time: string;
  status: number;
  error?: string;
  data?: T;
  validationErrors?: ValidationError[];
}

export function mapValidationErrors(data: any) {
  if (isArray(data)) {
    return data;
  }
  return map(keys(data), (key) => ({ field: key, message: data[key] }));
}

function handleResponse<T, R>(
  response: AxiosResponse<Response<T>>,
  Type: new () => T
): Promise<Response<R>> {
  if (response.status == 403) {
    return Promise.resolve({ status: 403, data: null } as Response<R>);
  }

  const result = fromSnakeCase(response.data);
  if (response.status == 400) {
    return Promise.reject({
      status: 400,
      validationErrors: mapValidationErrors(result.data),
    });
  }

  if (response.status == 404) {
    return Promise.resolve({ ...result, data: null });
  }

  if (Type && result.data) {
    let data;

    if (Array.isArray(result.data)) {
      data = toTypeList(result.data, Type);
    } else {
      data = toType(result.data, Type);
    }
    return Promise.resolve({ ...result, data: data });
  }

  return Promise.resolve(result as any);
}

export default {
  axios: axios,

  get: <T>(path: string, responseType?: new () => T): Promise<Response<T>> =>
    axios
      .get<Response<T>>(path)
      .then((res) => handleResponse<T, T>(res, responseType)),

  getList: <T>(
    path: string,
    responseType?: new () => T
  ): Promise<Response<T[]>> =>
    axios
      .get<Response<T>>(path)
      .then((res) => handleResponse<T, T[]>(res, responseType)),

  post: <T>(
    path: string,
    data: any,
    responseType?: new () => T,
    snakeCase = true
  ): Promise<Response<T>> =>
    axios
      .post<Response<T>>(path, snakeCase ? toSnakeCase(data) : data)
      .then((res) => handleResponse<T, T>(res, responseType)),

  put: <T>(
    path: string,
    data: any,
    responseType?: new () => T,
    snakeCase = true
  ): Promise<Response<T>> =>
    axios
      .put<Response<T>>(path, snakeCase ? toSnakeCase(data) : data)
      .then((res) => handleResponse<T, T>(res, responseType)),

  patch: <T>(
    path: string,
    data: any,
    responseType?: new () => T,
    snakeCase = true
  ): Promise<Response<T>> =>
    axios
      .patch<Response<T>>(path, snakeCase ? toSnakeCase(data) : data)
      .then((res) => handleResponse<T, T>(res, responseType)),

  delete: <T>(
    path: string,
    responseType?: new () => T
  ): Promise<Response<any>> =>
    axios.delete(path).then((res) => handleResponse<T, T>(res, responseType)),

  query: <T>(
    operation: string,
    responseType?: (new () => T) | any[],
    variables?: any
  ): Promise<GraphqlResponse<T>> =>
    callGraphql('query', operation, responseType, variables),

  queryList: <T>(
    operation: string,
    responseType?: (new () => T) | any[],
    variables?: any
  ) => callGraphqlList('query', operation, responseType, variables),

  mutation: <T>(
    operation: string,
    responseType?: (new () => T) | any[],
    variables?: any
  ): Promise<GraphqlResponse<T>> =>
    callGraphql('mutation', operation, responseType, variables),

  mutationList: <T>(
    operation: string,
    responseType?: (new () => T) | any[],
    variables?: any
  ) => callGraphqlList('mutation', operation, responseType, variables),
};

export function toSnakeCase(data) {
  return snakeCaseConversion(data, stringToSnakeCase, objectToSnakeCase);
}

function stringToSnakeCase(string: string) {
  return string.replace(/([A-Z])/g, (x) => '_' + x.toLowerCase());
}

function objectToSnakeCase(object) {
  if (isArray(object))
    return map(object, (v) => (isObject(v) ? objectToSnakeCase(v) : v));

  return transform(object, (r, v, k) => {
    r[stringToSnakeCase(k.toString())] = isObject(v) ? objectToSnakeCase(v) : v;
  });
}

function snakeCaseConversion(data, stringFunc, objectFunc) {
  if (!data) return data;

  if (isString(data)) return stringFunc(data);

  if (isObject(data)) return objectFunc(data);

  return data;
}

export function fromSnakeCase(data) {
  return snakeCaseConversion(data, stringFromSnakeCase, objectFromSnakeCase);
}

function stringFromSnakeCase(string: string) {
  return string.replace(/(\_[a-z])/g, (x) => x.charAt(1).toUpperCase());
}

function objectFromSnakeCase(object) {
  if (isArray(object)) return map(object, fromSnakeCase);

  return transform(object, (r, v, k) => {
    r[stringFromSnakeCase(k.toString())] = isObject(v)
      ? objectFromSnakeCase(v)
      : v;
  });
}

function callGraphql<T>(
  type: GraphqlOperation,
  operation: string,
  responseType: (new () => T) | any[],
  variables?: any
): Promise<GraphqlResponse<T>> {
  return axios
    .post<Response<T>>(
      'graphql',
      buildGraphqlRequest(
        type,
        operation,
        buildGraphqlFields(responseType),
        variables
      )
    )
    .then((res) =>
      handleGraphqlResponse<T, T>(type, operation, res, responseType)
    );
}

function callGraphqlList<T>(
  type: GraphqlOperation,
  operation: string,
  responseType: (new () => T) | any[],
  variables?: any
): Promise<GraphqlResponse<T[]>> {
  return callGraphql(
    type,
    operation,
    responseType,
    variables
  ) as any as Promise<GraphqlResponse<T[]>>;
}

function buildGraphqlFields<T>(Type: (new () => T) | any[]): any[] {
  if (isArray(Type)) return Type as any;
  const types = (Type as any).types;

  const instance = new Type();
  return map(keys(toData(instance)), (key) => {
    if (types && types[key]) {
      return { [key]: buildGraphqlFields(types[key]) };
    }
    return key;
  });
}

function buildGraphqlRequest(
  type: GraphqlOperation,
  operation: string,
  fields: any[],
  variables: any
) {
  variables = replaceNullWithUndefined(variables);
  return gql[type]({ operation, variables, fields });
}

export interface GraphqlResponse<T> {
  error?: string;
  data?: T;
  validationErrors?: ValidationError[];
}

function handleGraphqlResponse<T, R>(
  type: GraphqlOperation,
  operation: string,
  response: AxiosResponse<any>,
  Type: (new () => T) | any[]
): Promise<GraphqlResponse<R>> {
  const result = response.data;

  if (result.errors && isArray(result.errors) && result.errors.length > 0) {
    if (type === 'mutation') {
      const validationErrors = filter(
        result.errors,
        (e) => e.field && e.message
      );
      if (validationErrors.length > 0) {
        Promise.reject({ validationErrors: validationErrors });
      }
    }
    if (!result.data[operation]) {
      return Promise.reject({ error: result.errors[0].message });
    }
  }

  let data = result.data[operation];

  if (Type && data) {
    if (Array.isArray(data)) {
      data = toTypeList(data, Type as new () => T);
    } else {
      data = toType(data, Type as new () => T);
    }
    return Promise.resolve({ data: data, errors: result.errors });
  }

  return Promise.resolve({ data: data, errors: result.errors });
}

function replaceNullWithUndefined(obj) {
  if (obj === null) return undefined;

  if (!isObject(obj) || !Object.keys(obj).length) return obj;

  return mapValues(obj, (v) => replaceNullWithUndefined(v));
}

const useFetch = <T>(
  method: 'post' | 'get',
  path: string,
  requestData = null
) => {
  const [data, setData] = useState<T>(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = () => {
    setLoading(true);
    const action =
      method === 'post'
        ? axios.post(path, toSnakeCase(requestData))
        : axios.get(path, { params: requestData });
    action
      .then((response) => setData(response.data))
      .catch((err) => setError(err))
      .then(() => setLoading(false));
  };

  useEffect(fetch, [path, requestData]);

  return { data, error, loading, refetch: fetch };
};

export function usePost<T>(path: string, body = null) {
  return useFetch<T>('post', path, body);
}

export function useGet<T>(path: string, params = null) {
  return useFetch<T>('get', path, params);
}
